// src/layouts/DashboardLayout.js
import React, { useState, useEffect } from 'react';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Toolbar, Typography, Button, CircularProgress,
  Chip, Alert
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  Campaign as MarketingIcon,
  Upgrade as UpgradeIcon,
  Group as UsersIcon,
  LocalHospital as ServicesIcon,
  AttachMoney as FinancialIcon,
  Description as FormsIcon,
  Timeline as ActivityIcon,
  Leaderboard as LeadsIcon,
  Assessment as ReportsIcon,
  Build as AutomationsIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import useAuthToken, { useLogout } from '../hooks/useAuthToken';
import { useAppTheme } from '../contexts/ThemeContext';
import { hasFeatureAccess, getUpgradeMessage } from '../utils/featureAccess';
import TrialWarningBanner from '../components/TrialWarningBanner';

// Get correct API base URL (same logic as api/client.js)
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    // Use proxy approach for better security - Vercel forwards /api/* to backend
    return '/api';
  }
};
// import SubscriptionNotificationDemo from '../components/SubscriptionNotificationDemo';

const drawerWidth = 240;

const AuthenticatedLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const logout = useLogout();
  const token = useAuthToken();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [cancellationInfo, setCancellationInfo] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const { currentTheme } = useAppTheme();
  const location = useLocation();

  useEffect(() => {
    if (!token) {
      console.log('No token found, redirecting to login.');
      navigate('/login');
    } else {
      setIsVerifying(false);
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        setSubscriptionTier(decoded.subscription_tier || 'free');

        // Extract cancellation information from JWT
        console.log('JWT decoded payload:', {
          cancel_at_period_end: decoded.cancel_at_period_end,
          current_period_end: decoded.current_period_end,
          subscription_tier: decoded.subscription_tier,
          subscription_status: decoded.subscription_status,
          clinic_id: decoded.clinic_id
        });

        // PROACTIVE SUBSCRIPTION EXPIRY CHECK
        // Check if subscription has expired or is canceled and force logout immediately

        // Check 1: Fully canceled subscription (subscription_status = "canceled")
        if (decoded.subscription_status === 'canceled') {
          console.warn('🚨 SUBSCRIPTION CANCELED: User subscription is fully canceled, forcing immediate logout');
          console.log(`   Subscription status: ${decoded.subscription_status}`);

          // Store clinic ID for expired subscription page before clearing tokens
          if (decoded.clinic_id) {
            localStorage.setItem('expired_clinic_id', decoded.clinic_id);
            console.log('💾 Stored clinic ID for expired subscription page:', decoded.clinic_id);
          }

          // Clear tokens and redirect to expired subscription page
          localStorage.removeItem('auth_token');
          localStorage.removeItem('authToken');
          window.location.href = '/expired-subscription';
          return; // Exit early to prevent further processing
        }

        // TEMPORARY TEST CHECK: For our specific test clinic, check if JWT is old and needs refresh
        if (decoded.clinic_id === 'cb27a185-3bbb-45f7-9a49-edd92cc36770') {
          console.log('🔍 TEST CLINIC DETECTED: Validating JWT token for Pabau Demo clinic');
          console.log('   Subscription status:', decoded.subscription_status);
          console.log('   Subscription tier:', decoded.subscription_tier);
          console.log('   Subscription ID:', decoded.subscription_id);
          console.log('   Customer ID:', decoded.customer_id);

          // Check for stale JWT token conditions:
          // 1. JWT doesn't have subscription_status field (old token)
          if (!decoded.subscription_status) {
            console.warn('🚨 OLD JWT DETECTED: JWT token missing subscription_status field, forcing logout for fresh token');
            console.log('   This JWT was created before subscription cancellation tracking was implemented');
            console.log('   User needs to log in again to get fresh token with current subscription status');

            // Clear tokens and redirect to login page to get fresh token
            localStorage.removeItem('auth_token');
            localStorage.removeItem('authToken');
            window.location.href = '/login';
            return; // Exit early to prevent further processing
          }

          // Check for stale token: JWT shows active/free but we know this clinic is canceled
          // UPDATED: Only trigger for truly stale tokens (free tier with no subscription data)
          if (decoded.subscription_status === 'active' && decoded.subscription_tier === 'free' &&
            (!decoded.subscription_id || decoded.subscription_id === null) &&
            (!decoded.customer_id || decoded.customer_id === null)) {
            console.warn('🚨 STALE JWT TOKEN: Token shows free/active but clinic should have subscription data');
            console.log('   This indicates the JWT token needs to be refreshed with current subscription status');
            console.log(`   Token data: status=${decoded.subscription_status}, tier=${decoded.subscription_tier}, id=${decoded.subscription_id}`);

            // Force a fresh login to get updated JWT with correct subscription status
            localStorage.removeItem('auth_token');
            localStorage.removeItem('authToken');
            window.location.href = '/login?reason=token_refresh&message=Please log in again to refresh your session';
            return; // Exit early to prevent further processing
          }

          // If we get here, the token looks valid for this test clinic
          console.log('✅ TEST CLINIC JWT TOKEN VALID: Proceeding with normal authentication flow');
        }

        // Check 2: Scheduled cancellation that has now expired (cancel_at_period_end = true + past end date)
        if (decoded.cancel_at_period_end && decoded.current_period_end) {
          const periodEnd = new Date(decoded.current_period_end * 1000); // Convert Unix timestamp
          const now = new Date();
          const daysRemaining = Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24));

          console.log('Subscription expiry check:', {
            periodEnd: periodEnd.toISOString(),
            now: now.toISOString(),
            hasExpired: now > periodEnd,
            daysRemaining: daysRemaining
          });

          // If subscription has expired, immediately log out user
          if (now > periodEnd) {
            console.warn('🚨 SUBSCRIPTION EXPIRED: User subscription expired, forcing immediate logout');
            console.log(`   Expired on: ${periodEnd.toISOString()}`);
            console.log(`   Current time: ${now.toISOString()}`);

            // Clear tokens and redirect to expired subscription page
            localStorage.removeItem('auth_token');
            localStorage.removeItem('authToken');
            window.location.href = '/expired-subscription';
            return; // Exit early to prevent further processing
          }

          console.log('Setting cancellation info:', {
            cancelAtPeriodEnd: decoded.cancel_at_period_end,
            periodEnd: periodEnd,
            daysRemaining: Math.max(0, daysRemaining)
          });

          setCancellationInfo({
            cancelAtPeriodEnd: decoded.cancel_at_period_end,
            periodEnd: periodEnd,
            daysRemaining: Math.max(0, daysRemaining)
          });
        } else {
          console.log('No cancellation info found in JWT, setting to null');
          setCancellationInfo(null);
        }
      } catch (error) {
        console.error('Error decoding token for subscription tier:', error);
        setSubscriptionTier('free');
        setCancellationInfo(null);
      }
    }
  }, [token, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Check for dev mode flag in localStorage
  const showDevMenu = localStorage.getItem('show_all_menu_items') === 'true';

  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, feature: 'dashboard', devOnly: true },
    { label: 'Clients', path: '/clients', icon: <PeopleIcon />, feature: 'clients' },
    { label: 'Schedule', path: '/calendar', icon: <CalendarIcon />, feature: 'calendar' },
    { label: 'Services', path: '/services', icon: <ServicesIcon />, feature: 'services', devOnly: true },
    { label: 'Financial', path: '/financial', icon: <FinancialIcon />, feature: 'financial', devOnly: true },
    { label: 'Forms', path: '/forms', icon: <FormsIcon />, feature: 'forms', devOnly: true },
    { label: 'Leads', path: '/leads', icon: <LeadsIcon />, feature: 'leads', devOnly: true },
    { label: 'Reports', path: '/reports', icon: <ReportsIcon />, feature: 'reports', devOnly: true },
    { label: 'Activity', path: '/activity', icon: <ActivityIcon />, feature: 'activity', devOnly: true },
    { label: 'Marketing', path: '/marketing-dashboard', icon: <MarketingIcon />, feature: 'marketing_dashboard', upcoming: true, devOnly: true },
    { label: 'Automations', path: '/automations', icon: <AutomationsIcon />, feature: 'automations', devOnly: true },
    { label: 'Users & Staff', path: '/users', icon: <UsersIcon />, feature: 'users', devOnly: true },
    { label: 'Settings', path: '/settings', icon: <SettingsIcon />, feature: 'settings' }
  ];

  const accessibleItems = navigationItems.filter(item => {
    // Filter out devOnly items unless showDevMenu is true
    if (item.devOnly && !showDevMenu) return false;
    return hasFeatureAccess(item.feature);
  });

  const renderMenuItem = (item, index) => {
    const hasAccess = hasFeatureAccess(item.feature);
    if (!hasAccess) return null;

    const selected = location.pathname.startsWith(item.path);
    const handleClick = (e) => {
      if (item.upcoming) {
        e.preventDefault();
        // Route to generic upcoming page with path param
        if (item.path.includes('marketing')) navigate('/upcoming/marketing');
        else if (item.path.includes('reports')) navigate('/upcoming/reports');
        return;
      }
      navigate(item.path);
    };

    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          component={Link}
          to={item.path}
          onClick={handleClick}
          selected={selected}
          sx={{
            my: 0.5,
            mx: 1,
            borderRadius: 1,
            ...(index % 2 === 1 ? { bgcolor: 'action.hover' } : {}),
            '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText' },
            '&:hover': { bgcolor: 'action.selected' }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: selected ? 'primary.contrastText' : 'text.secondary' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: selected ? 700 : 600, fontSize: 14 }}>
                  {item.label}
                </Typography>
                {item.upcoming && (
                  <Chip label="SOON" size="small" color="warning" variant="outlined" sx={{ height: 18 }} />
                )}
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <div>
      <Toolbar />
      <List>
        {accessibleItems.map((item, index) => renderMenuItem(item, index))}

        {/* Show upgrade prompt for non-enterprise users at bottom of menu */}
        {(subscriptionTier === 'free' || subscriptionTier === 'basic' || subscriptionTier === 'pro') && (
          <ListItem sx={{ mt: 2, px: 2 }}>
            <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'center', color: 'primary.contrastText' }}>
              <UpgradeIcon sx={{ mb: 1 }} />
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                Unlock more features
              </Typography>
              <Button size="small" variant="contained" color="secondary" onClick={() => window.open('/pricing', '_blank')}>
                Upgrade Plan
              </Button>
            </Box>
          </ListItem>
        )}
      </List>
    </div>
  );

  if (isVerifying) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundImage: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, boxShadow: 3, borderRadius: 0 }}>
        <Toolbar sx={{ minHeight: 64, px: 2 }}>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700, letterSpacing: .3 }}>
            MedInsight Dashboard
          </Typography>
          <Box sx={{ flexGrow: 1 }} />

          {/* Show tier badge in header for non-enterprise users */}
          {(subscriptionTier === 'free' || subscriptionTier === 'basic' || subscriptionTier === 'pro') && (
            <Chip
              label={subscriptionTier === 'free' ? 'Free Plan' : subscriptionTier === 'basic' ? 'Basic Plan' : 'Pro Plan'}
              size="small"
              color="secondary"
              sx={{ mr: 2 }}
            />
          )}

          {/* Debug buttons - Hidden in production */}
          {process.env.NODE_ENV === 'development' && (
            <>
              <Button color="inherit" onClick={() => setDebugMode(!debugMode)}>
                Debug
              </Button>
              <Button color="inherit" onClick={async () => {
                try {
                  const response = await fetch(`${getApiBaseUrl()}/billing/refresh-token`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (response.ok) {
                    const data = await response.json();
                    localStorage.setItem('authToken', data.access_token);
                    localStorage.setItem('auth_token', data.access_token);
                    window.location.reload();
                  }
                } catch (error) {
                  console.error('Token refresh failed:', error);
                }
              }}>
                Refresh Token
              </Button>
            </>
          )}
          <Button color="inherit" variant="outlined" startIcon={<LogoutIcon />} onClick={logout} sx={{ borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'common.white', backgroundColor: 'rgba(255,255,255,0.08)' } }}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none' } }} open>
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, backgroundColor: '#f5f5f5' }}>
        <Toolbar sx={{ minHeight: 64 }} />
        <TrialWarningBanner />

        {/* Debug Mode Panel */}
        {debugMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6">Debug Info</Typography>
            <Typography variant="body2">Token exists: {token ? 'Yes' : 'No'}</Typography>
            <Typography variant="body2">Cancellation Info: {JSON.stringify(cancellationInfo)}</Typography>
            <Typography variant="body2">Subscription Tier: {subscriptionTier}</Typography>
          </Alert>
        )}

        {/* Cancellation Status Banner */}
        {cancellationInfo && cancellationInfo.cancelAtPeriodEnd && (
          <Alert
            severity="warning"
            icon={<ScheduleIcon />}
            sx={{
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            <Typography component="span">
              <strong>Subscription Ending Soon:</strong> Your access expires on{' '}
              <strong>{cancellationInfo.periodEnd.toLocaleDateString()}</strong>
              {cancellationInfo.daysRemaining > 0 && (
                <span> ({cancellationInfo.daysRemaining} day{cancellationInfo.daysRemaining !== 1 ? 's' : ''} remaining)</span>
              )}
              {cancellationInfo.daysRemaining === 0 && <span> (expires today)</span>}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              Reactivate your subscription in Settings to maintain access to all features.
            </Typography>
          </Alert>
        )}

        <Outlet />

        {/* Subscription Notification Demo - Development Only - Currently Hidden */}
        {/* <SubscriptionNotificationDemo /> */}
      </Box>
    </Box>
  );
};

export default AuthenticatedLayout;